import { useState, useEffect } from 'react';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import ClearFiltersButton from '../components/common/ClearFiltersButton';
import { useUrlParams } from '../hooks/useUrlState';
import StateBlock from '../components/StateBlock';
import EntityLink from '../components/EntityLink';

const BUCKET_COLORS = {
  'Not Due Yet': 'var(--muted)',
  '1-30 Days': '#e0a030',
  '31-60 Days': '#e08030',
  '61-90 Days': '#d06040',
  '90+ Days': 'var(--danger, #d64545)',
};

function fmt(n) {
  return '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function AgingReportPage() {
  const { get, set } = useUrlParams();
  const bucketFilter = get('bucket', 'all');
  const search = get('search', '');
  // 'consulting' = Business Consulting, 'staffing' = Hexaware staffing (2026-10-02)
  const segment = get('segment', 'all');
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/aging-report').then(r => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) {
    return <StateBlock type="error" action={<button className="btn btn-primary" onClick={() => window.location.reload()}>Try again</button>} />;
  }
  if (!data) return <StateBlock type="loading" />;

  const segRows = data.rows.filter(r => segment === 'all' || r.segment === segment);
  const round2 = n => Math.round(n * 100) / 100;
  const summary = data.summary.map(s => {
    const inB = segRows.filter(r => r.aging_bucket === s.bucket);
    return { bucket: s.bucket, count: inB.length, outstanding: round2(inB.reduce((t, r) => t + r.outstanding, 0)) };
  });
  const totalOutstanding = round2(segRows.reduce((t, r) => t + r.outstanding, 0));
  const filtered = segRows.filter(r => {
    if (bucketFilter !== 'all' && r.aging_bucket !== bucketFilter) return false;
    const s = search.toLowerCase();
    return !s || r.invoice_number.toLowerCase().includes(s) || (r.contractor_name || '').toLowerCase().includes(s) || (r.client_name || '').toLowerCase().includes(s) || (r.client_wo_number || '').toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Aging Report</h1>
          <p className="page-subtitle">Real, outstanding standard and Business Consulting AR invoices, grouped by days past due. Uses due date where set, otherwise invoice date plus the client's net terms.</p>
        </div>
        <button className="btn btn-secondary" onClick={() => downloadCsv('/aging-report/export', 'aging-report.csv', exportIdsParam(filtered, data.rows))}>Download CSV</button>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        <div
          className="card"
          style={{ background: bucketFilter === 'all' ? 'var(--accent-subtle, #f0f4ff)' : 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 16, cursor: 'pointer' }}
          onClick={() => set({ bucket: 'all' })}
        >
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>All Outstanding</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{fmt(totalOutstanding)}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{segRows.length} invoices</div>
        </div>
        {summary.map(s => (
          <div
            key={s.bucket}
            className="card"
            style={{ background: bucketFilter === s.bucket ? 'var(--accent-subtle, #f0f4ff)' : 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 16, cursor: 'pointer' }}
            onClick={() => set({ bucket: s.bucket })}
          >
            <div style={{ fontSize: 12, color: BUCKET_COLORS[s.bucket], fontWeight: 600 }}>{s.bucket}</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{fmt(s.outstanding)}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.count} invoices</div>
          </div>
        ))}
      </div>

      <div className="filter-bar">
        <input placeholder="Search invoice #, contractor, client, WO#…" value={search} onChange={e => set({ search: e.target.value })} style={{ flex: 1 }} />
        <select value={segment} onChange={e => set({ segment: e.target.value === 'all' ? '' : e.target.value })}>
          <option value="all">Hexaware + Consulting</option>
          <option value="staffing">Hexaware staffing only</option>
          <option value="consulting">Business Consulting only</option>
        </select>
        <ClearFiltersButton active={!!(search || bucketFilter !== 'all' || segment !== 'all')} onClear={() => set({ search: '', bucket: '', segment: '' })} />
      </div>

      {filtered.length === 0 ? (
        <StateBlock type="empty" message="No outstanding invoices match." />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Contractor / Client</th>
              <th>WO #</th>
              <th>Period</th>
              <th>Due Date</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th style={{ textAlign: 'right' }}>Paid</th>
              <th style={{ textAlign: 'right' }}>Outstanding</th>
              <th style={{ textAlign: 'right' }}>Days Past Due</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id}>
                <td><EntityLink type="ar_invoice" id={r.id}>{r.invoice_number}</EntityLink></td>
                <td>{r.segment === 'consulting'
                  ? <><EntityLink type="client" id={r.client_id}>{r.client_name}</EntityLink> <span style={{ fontSize: 11, color: 'var(--muted)' }}>· Consulting</span></>
                  : <EntityLink type="contractor" id={r.contractor_id}>{r.contractor_name}</EntityLink>}</td>
                <td>{r.client_wo_number}</td>
                <td>{r.period_start}</td>
                <td>{r.effective_due_date}</td>
                <td style={{ textAlign: 'right' }}>{fmt(r.amount)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(r.verified_paid)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.outstanding)}</td>
                <td style={{ textAlign: 'right' }}>{r.days_past_due < 0 ? `${-r.days_past_due} days early` : `${r.days_past_due} days`}</td>
                <td>
                  <span style={{ color: BUCKET_COLORS[r.aging_bucket], fontWeight: 600, fontSize: 12 }}>
                    {r.aging_bucket}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
